// AI team controller — strategic decisions for AI-managed cars.
// Distinct from the driver AI (ai-controller.js): the driver's job
// is "drive the car this tick" (throttle / brake / steer); the team's
// job is "decide what the car should be doing this stint" (pit call,
// drive mode, tire compound, etc).
//
// The split mirrors real motorsport: the engineer / strategist on the
// pit wall owns pit calls and pace targets, the driver executes them.
// In god mode the player IS the strategist for every car (the team-
// control HUD targets the selected car). In solo mode the player only
// owns one team's decisions; this module fills in for everyone else.
//
// V1 scope is intentionally small: pit when tires are worn, fuel is
// insufficient, or damage is significant. Future expansion goes here
// — drive-mode swaps (push at the start of a stint, conserve at the
// end), tire-compound choices on stop, undercut/overcut decisions,
// per-driver / per-team personality. The module is pure: takes a car
// + world, mutates pit state in place, returns nothing.

// Tire-life floor below which the AI commits to pitting. 0.25 = 25 %
// remaining — late enough that the car has used most of the rubber,
// early enough that grip hasn't fallen off a cliff yet (the wear-grip
// curve in condition.js floors out at TIRE_GRIP_FLOOR but the
// tireReserveFactor in ai-controller widens cornering margins on
// worn tires, so lap times drop noticeably below ~30 %).
export const TIRE_PIT_THRESHOLD = 0.25;

// Fuel headroom (laps) — pit when we have less than this many laps
// of fuel left and we won't make the flag without stopping. A small
// buffer covers a worst-case in / out lap mis-time plus the heavier
// early-stint fuel burn before drag savings kick in.
export const FUEL_BUFFER_LAPS = 2;

// Throttle duty-cycle estimate. spec.fuelBurnRate is the kg/s burn
// at FULL throttle (see condition.js: actual burn = throttle ×
// fuelBurnRate × dt). Real lap throttle averages well below 1.0
// (corner braking, partial throttle on entries). 0.6 is a sane
// "hot lap" average — slightly conservative so the AI errs toward
// pitting a lap early rather than running dry. Replace with a
// per-car measured burn-per-lap when that lands.
const FUEL_DUTY_CYCLE = 0.6;

// Damage threshold for a repair stop. Above 30 % damage the engine
// performance multiplier is starting to bite (the linear health-cliff
// curve in condition.js drops perfMul to ~70 % at 30 % damage, and
// keeps falling). Worth a 30 s/% repair to claw it back.
export const DAMAGE_PIT_THRESHOLD = 0.30;

// Default lap time used when the car hasn't completed one yet (lap 0,
// or just spawned). Conservative — overestimates fuel-per-lap so the
// AI errs toward earlier pit calls during the first stint.
const DEFAULT_LAP_TIME_S = 90;

// Make a pit-call decision for one AI-managed car. Mutates
// car.pitState.requested + car.pitState.order in place when the AI
// decides to pit; otherwise leaves state untouched. Caller decides
// the cadence (every tick is fine but wasteful — once per second is
// the natural granularity for strategic checks).
//
// Skipped silently for cars not in 'racing' phase (already pitting,
// stopped at the box, or post-checkered), retired cars, and tracks
// without an authored pit lane. Anyone else gets evaluated.
export function teamAIUpdate(car, world) {
  if (!car || !car.pitState) return;
  if (car.pitState.phase !== 'racing') return;
  if (car._raceFinished) return;
  if (!world?.geometry?.pitRoute) return;

  const cond = car.condition;
  const ls   = car.lapState;
  const rs   = world.raceState;
  if (!cond || !ls || !rs) return;

  // No AI pit calls once the leader is on the final lap (or beyond).
  // Gating on the LEADER's lap rather than this car's catches lapped
  // cars too: when the leader is on lap 20 of 20, every car still
  // circulating is one S/F crossing away from being classified —
  // nothing they can do in the pits earns back the lost time. Also
  // cancel any pre-existing request: the AI may have decided to pit
  // a lap or two earlier, but the strategic case has flipped now that
  // the checkered is in sight. We're inside the solo-mode AI-managed
  // branch (see main.js call site), so the player's own car is never
  // touched and we can safely overwrite `requested` for everyone we
  // see here. Phase=cooldown is also caught — by then the leader has
  // already taken the flag and any remaining circulator is rolling
  // through their classification crossing.
  let leaderLap = 0;
  for (const c of world.cars) {
    const lap = c.lapState?.lapNumber ?? 0;
    if (lap > leaderLap) leaderLap = lap;
  }
  const finalLap = (rs.totalLaps ?? 0) > 0 && leaderLap >= rs.totalLaps;
  if (finalLap || rs.phase === 'cooldown') {
    if (car.pitState.requested) car.pitState.requested = false;
    return;
  }

  if (car.pitState.requested) return;

  // ---- tire concern ----
  // Sticky once tripped — once tireLife is below the threshold, the
  // car will pit on the next opportunity. Blown tires count too: the
  // car needs new rubber regardless of remaining life percentage.
  const tireConcern = cond.tireLife < TIRE_PIT_THRESHOLD || cond.blownTires === true;

  // ---- fuel concern ----
  // Two things have to be true to pit for fuel:
  //   1. The remaining fuel won't reach the flag (otherwise we'd pit
  //      right after a stop just because total race fuel exceeds tank
  //      capacity — the bug we hit when the AI pitted every lap).
  //   2. Fuel is actually low (within FUEL_BUFFER_LAPS of empty),
  //      meaning it's the right time to stop. Pitting on lap 5 of 30
  //      with a half-tank is wasteful even if we know we'll need
  //      another stop eventually.
  //
  // Per-lap burn estimate: spec.fuelBurnRate × FUEL_DUTY_CYCLE × lap
  // time. The duty-cycle factor accounts for partial-throttle driving
  // (corners, braking) — without it we over-estimate burn by ~40 %
  // and the AI sees a phantom fuel deficit even with a full tank.
  // `leaderLap` was already computed for the final-lap cancel above.
  const lapsLeft       = Math.max(0, rs.totalLaps - leaderLap + 1);
  const burnPerSec     = (car.spec?.fuelBurnRate ?? 0.05) * FUEL_DUTY_CYCLE;
  const lastLapMs      = ls.lastLapTime;
  const lapTimeS       = lastLapMs != null && lastLapMs > 0 ? lastLapMs / 1000 : DEFAULT_LAP_TIME_S;
  const burnPerLap     = burnPerSec * lapTimeS;
  const fuelToFinish   = lapsLeft * burnPerLap;
  const fuelLowBuffer  = FUEL_BUFFER_LAPS * burnPerLap;
  const cantReachFlag  = cond.fuel < fuelToFinish;
  const tankNearEmpty  = cond.fuel < fuelLowBuffer;
  const fuelConcern    = cantReachFlag && tankNearEmpty;

  // ---- damage concern ----
  // Repair-only stops are wasteful — only pit for damage if the
  // damage is meaningful. The body-fragility cliff at 50 % is
  // catastrophic; the AI bails well before that.
  const damageConcern = cond.damage > DAMAGE_PIT_THRESHOLD;

  if (!tireConcern && !fuelConcern && !damageConcern) return;

  // Compose the order. Tires get changed on any stop (fresh rubber is
  // free if you're already stopped); fuel only if the deficit
  // triggered; repair only if damage triggered. The pit-stop service
  // time math in main.js takes the max of tire-time and fuel-time, so
  // adding tires when only fuel was needed costs us nothing extra.
  car.pitState.order.tires  = true;
  car.pitState.order.fuel   = fuelConcern;
  car.pitState.order.repair = damageConcern;
  car.pitState.requested    = true;
}
